import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Input, InputEvent } from '@/components/form/Input';
import { Modal } from '@/components/Modal';
import { Agent } from '@/hooks/useAgents';
import {
  getValidationErrorMessage,
  validateUsername,
} from '@/utils/validation.util';

import { useUserManagement } from './useUserManagement';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  onSuccess?: (taskId?: string) => void;
}

/**
 * Modal for adding a Linux user to an Aegis agent.
 *
 * Extracted from UserManagementTab to enable direct modal access from row actions
 * following information architecture best practices (colocation, reusability).
 *
 * Can be opened from:
 * - Row action "Add User" (direct modal access)
 * - Users tab "Add User" button (within drawer)
 */
export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  agent,
  onSuccess,
}) => {
  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userManagement = useUserManagement(isOpen);

  // Reset username when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUsername('');
    }
  }, [isOpen]);

  const handleAdd = async () => {
    if (!agent || !username) return;

    // Validate username format
    const validationResult = validateUsername(username);
    if (!validationResult.isValid) {
      toast.error(getValidationErrorMessage(validationResult));
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await userManagement.addUser(agent.client_id, username);

      // Reset and close
      setUsername('');
      onClose();

      // Notify parent of success
      onSuccess?.(response.taskId);
    } catch (error) {
      console.error('Failed to add user:', error);
      // Error handling is managed by the user management hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        setUsername('');
        onClose();
      }}
      title="Add User"
      footer={{
        text: isSubmitting ? 'Adding...' : 'Add User',
        styleType: 'primary',
        onClick: handleAdd,
        disabled: isSubmitting || !username.trim(),
      }}
      data-testid="add-user-modal"
    >
      <div className="flex flex-col gap-4">
        <p className="text-chariot-text-secondary text-sm">
          Add a Linux user with a Praetorian email address. The username format
          should be: first.last (e.g., john.doe)
        </p>
        <Input
          name="username"
          label="Username"
          value={username}
          onChange={(e: InputEvent) => setUsername(e.target.value)}
          placeholder="first.last"
        />
      </div>
    </Modal>
  );
};
